import {
  Button,
  Card,
  Cascader,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
  productWantRequestDelete,
  productWantRequestList,
} from 'apis/product-want-request';
import usePagination from 'commons/hooks/usePagination';
import LOCALS from 'commons/locals';
import showTotal from 'components/show-total';
import dayjs from 'dayjs';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState,
} from 'nuqs';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { selectUserInfo } from 'store/slices/userInfoSlice';
import {
  PmsProductWantRequestRecord,
  PmsProductWantRequestSource,
  PmsProductWantRequestStatus,
} from 'types/pms-product-want-request';
import EditDrawer from './edit-drawer';
import MatchDrawer from './match-drawer';
import OperateLogDrawer from './operate-log-drawer';
import StatusUpdateModal from './status-update-modal';
import getCascaderFilter from 'utils/getCascaderFilter';
import findCascaderOptionById from 'utils/findCascaderOptionById';
import { DefaultOptionType, SelectOption } from 'types/base';
import {
  getWantRequestSourceLabel,
  getWantRequestSourceSelectOptions,
  getWantRequestStatusSelectOptions,
  getMaterialOptionsForCategory,
  parseCategoryCascaderPath,
  parseMaterialCascaderPath,
} from './common';

const TagCell = ({ children }: { children: ReactNode }) => (
  <div className="flex max-w-full flex-wrap gap-1 py-0.5">{children}</div>
);

const SpecRow = ({
  label,
  children,
}: {
  label: ReactNode;
  children: ReactNode;
}) => (
  <div className="flex gap-1 text-sm">
    <span className="shrink-0 text-gray-500">{label}:</span>
    <div className="min-w-0 flex-1">{children}</div>
  </div>
);

const renderOptionTags = (
  codes: string[] | null | undefined,
  options: SelectOption[],
) => {
  if (!codes?.length) return '-';

  return (
    <TagCell>
      {codes.map((code) => {
        const label = options.find((d) => d.value === code)?.label || code;
        return (
          <Tag key={code} className="m-0 max-w-full whitespace-normal">
            {label}
          </Tag>
        );
      })}
    </TagCell>
  );
};

const useProductWantRequestQueryParams = () => {
  const [pageNum, setPageNum] = useQueryState(
    'pageNum',
    parseAsInteger.withDefault(1),
  );
  const [pageSize, setPageSize] = useQueryState(
    'pageSize',
    parseAsInteger.withDefault(10),
  );
  const [email, setEmail] = useQueryState(
    'email',
    parseAsString.withDefault(''),
  );
  const [phone, setPhone] = useQueryState(
    'phone',
    parseAsString.withDefault(''),
  );
  const [status, setStatus] = useQueryState(
    'status',
    parseAsString.withDefault(''),
  );
  const [source, setSource] = useQueryState(
    'source',
    parseAsString.withDefault(''),
  );
  const [productCategoryId, setProductCategoryId] = useQueryState(
    'productCategoryId',
    parseAsInteger,
  );
  const [ranks, setRanks] = useQueryState(
    'ranks',
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const [color, setColor] = useQueryState(
    'color',
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const [hardware, setHardware] = useQueryState(
    'hardware',
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const [stamp, setStamp] = useQueryState(
    'stamp',
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const [materials, setMaterials] = useQueryState(
    'materials',
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const [remark, setRemark] = useQueryState(
    'remark',
    parseAsString.withDefault(''),
  );

  return {
    pageNum,
    pageSize,
    email,
    phone,
    status,
    source,
    productCategoryId,
    ranks,
    color,
    hardware,
    stamp,
    materials,
    remark,
    setPageNum,
    setPageSize,
    setEmail,
    setPhone,
    setStatus,
    setSource,
    setProductCategoryId,
    setRanks,
    setColor,
    setHardware,
    setStamp,
    setMaterials,
    setRemark,
  };
};

const ProductWantRequestPage = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAppSelector(selectUserInfo);
  const {
    productCategoryCascaderOptions,
    rankSelectOptions,
    colorSelectOptions,
    hardwareSelectOptions,
    stampSelectOptions,
    materialCascaderOptionsMap,
  } = useAppSelector(selectGlobalInfo);

  const statusSelectOptions = useMemo(
    () => getWantRequestStatusSelectOptions(t),
    [t],
  );

  const sourceSelectOptions = useMemo(
    () => getWantRequestSourceSelectOptions(t),
    [t],
  );

  const {
    pageNum,
    pageSize,
    email,
    phone,
    status,
    source,
    productCategoryId,
    ranks,
    color,
    hardware,
    stamp,
    materials,
    remark,
    setPageNum,
    setPageSize,
    setEmail,
    setPhone,
    setStatus,
    setSource,
    setProductCategoryId,
    setRanks,
    setColor,
    setHardware,
    setStamp,
    setMaterials,
    setRemark,
  } = useProductWantRequestQueryParams();

  const { loading, setLoading, dataSource, setDataSource, total, setTotal } =
    usePagination<PmsProductWantRequestRecord>();

  const [editRecord, setEditRecord] = useState<PmsProductWantRequestRecord>();
  const [createOpen, setCreateOpen] = useState(false);
  const [matchRequest, setMatchRequest] = useState<{
    id: number;
    email: string;
  }>();
  const [operateLogRequestId, setOperateLogRequestId] = useState<number>();
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    id: number;
    fromStatus: PmsProductWantRequestStatus;
    toStatus: PmsProductWantRequestStatus;
  }>();

  const [form] = Form.useForm<{
    email?: string;
    phone?: string;
    status?: PmsProductWantRequestStatus;
    source?: PmsProductWantRequestSource;
    productCategoryIdList?: number[];
    ranks?: string[];
    color?: string[];
    hardware?: string[];
    stamp?: string[];
    materialPaths?: (string | number)[][];
    remark?: string;
  }>();

  const filterCategoryIdList = Form.useWatch('productCategoryIdList', form);
  const filterFirstLevelId = filterCategoryIdList?.[0];

  const filterMaterialCascaderOptions = useMemo(() => {
    if (!filterFirstLevelId) return [];
    return materialCascaderOptionsMap[filterFirstLevelId] ?? [];
  }, [filterFirstLevelId, materialCascaderOptionsMap]);

  const rankOptions = useMemo(() => {
    if (!rankSelectOptions?.length) return [];
    return [...rankSelectOptions].sort((a, b) => (b.sort ?? 0) - (a.sort ?? 0));
  }, [rankSelectOptions]);

  const hardwareOptions = useMemo(() => {
    if (!hardwareSelectOptions?.length) return [];
    return [...hardwareSelectOptions].sort(
      (a, b) => (b.sort ?? 0) - (a.sort ?? 0),
    );
  }, [hardwareSelectOptions]);

  const stampOptions = useMemo(() => {
    if (!stampSelectOptions?.length) return [];
    return [...stampSelectOptions].sort(
      (a, b) => (b.sort ?? 0) - (a.sort ?? 0),
    );
  }, [stampSelectOptions]);

  const cascaderFilter = useCallback(
    (inputValue: string, path: DefaultOptionType[]) =>
      getCascaderFilter(inputValue, path),
    [],
  );

  const optionFilter = useCallback(
    (input: string, option?: SelectOption) =>
      (option?.label ?? '').toLowerCase().includes(input.toLowerCase()),
    [],
  );

  const getDataSource = useCallback(async () => {
    try {
      setLoading(true);
      const statusFilter = Object.values(PmsProductWantRequestStatus).includes(
        status as PmsProductWantRequestStatus,
      )
        ? (status as PmsProductWantRequestStatus)
        : undefined;
      const sourceFilter = Object.values(PmsProductWantRequestSource).includes(
        source as PmsProductWantRequestSource,
      )
        ? (source as PmsProductWantRequestSource)
        : undefined;
      const { list, total: totalCount } = await productWantRequestList({
        pageNum,
        pageSize,
        email: email || undefined,
        phone: phone || undefined,
        productCategoryId: productCategoryId ?? undefined,
        ranks: ranks.length ? ranks : undefined,
        color: color.length ? color : undefined,
        hardware: hardware.length ? hardware : undefined,
        stamp: stamp.length ? stamp : undefined,
        materials: materials.length ? materials : undefined,
        remark: remark || undefined,
        status: statusFilter,
        source: sourceFilter,
      });
      setDataSource(list);
      setTotal(totalCount);
    } finally {
      setLoading(false);
    }
  }, [
    color,
    email,
    hardware,
    materials,
    pageNum,
    pageSize,
    phone,
    productCategoryId,
    ranks,
    remark,
    stamp,
    status,
    source,
    setDataSource,
    setLoading,
    setTotal,
  ]);

  const handleStatusSelect = useCallback(
    (
      row: PmsProductWantRequestRecord,
      nextStatus: PmsProductWantRequestStatus,
    ) => {
      if (nextStatus === row.status) return;
      setPendingStatusChange({
        id: row.id,
        fromStatus: row.status,
        toStatus: nextStatus,
      });
    },
    [],
  );

  useEffect(() => {
    if (!productCategoryCascaderOptions.length) return;

    const categoryLeaf = productCategoryId
      ? findCascaderOptionById(
          productCategoryId,
          productCategoryCascaderOptions,
        )
      : undefined;
    const productCategoryIdList = categoryLeaf?.treeIds
      ? parseCategoryCascaderPath(categoryLeaf.treeIds)
      : productCategoryId
        ? [productCategoryId]
        : undefined;

    const materialOpts = productCategoryId
      ? getMaterialOptionsForCategory(
          productCategoryId,
          productCategoryCascaderOptions,
          materialCascaderOptionsMap,
        )
      : [];
    const materialPaths = (materials ?? []).map((leaf) => {
      const target = findCascaderOptionById(leaf, materialOpts);
      if (target?.treeIds) {
        return parseMaterialCascaderPath(target.treeIds);
      }
      return [leaf];
    });

    form.setFieldsValue({
      email,
      phone,
      status: Object.values(PmsProductWantRequestStatus).includes(
        status as PmsProductWantRequestStatus,
      )
        ? (status as PmsProductWantRequestStatus)
        : undefined,
      source: Object.values(PmsProductWantRequestSource).includes(
        source as PmsProductWantRequestSource,
      )
        ? (source as PmsProductWantRequestSource)
        : undefined,
      productCategoryIdList,
      ranks: ranks.length ? ranks : undefined,
      color: color.length ? color : undefined,
      hardware: hardware.length ? hardware : undefined,
      stamp: stamp.length ? stamp : undefined,
      materialPaths: materialPaths.length ? materialPaths : undefined,
      remark: remark || undefined,
    });
  }, [
    color,
    email,
    hardware,
    materials,
    phone,
    productCategoryCascaderOptions,
    productCategoryId,
    materialCascaderOptionsMap,
    ranks,
    remark,
    stamp,
    status,
    source,
    form,
  ]);

  useEffect(() => {
    getDataSource();
  }, [getDataSource]);

  const handleDelete = useCallback(
    async (id: number) => {
      await productWantRequestDelete(id);
      message.success(t('successful_operation'));
      getDataSource();
    },
    [getDataSource, t],
  );

  const columns: ColumnsType<PmsProductWantRequestRecord> = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        width: 80,
        render: (id: number, row) => (
          <Space size={4}>
            <span>{id}</span>
            {isAdmin && row.deleteStatus === 1 ? (
              <Tag color="error" className="m-0">
                <Trans i18nKey={LOCALS.delete} />
              </Tag>
            ) : null}
          </Space>
        ),
      },
      {
        title: <Trans i18nKey={LOCALS.status} />,
        dataIndex: 'status',
        width: 140,
        render: (value: PmsProductWantRequestStatus, row) => (
          <Select
            size="small"
            className="min-w-[120px] max-w-full"
            value={value}
            options={statusSelectOptions}
            popupMatchSelectWidth={false}
            onChange={(nextStatus) => handleStatusSelect(row, nextStatus)}
          />
        ),
      },
      {
        title: <Trans i18nKey={LOCALS.member} />,
        width: 200,
        render: (_, row) => (
          <div className="space-y-1 text-sm">
            <div>
              {row.memberId ? (
                <Link to={`/ums/member-view/${row.memberId}`}>
                  {row.member?.email ?? row.memberId}
                </Link>
              ) : (
                '-'
              )}
            </div>
            <div>{row.email || '-'}</div>
            <div>{row.phone || '-'}</div>
          </div>
        ),
      },
      {
        title: <Trans i18nKey={LOCALS.product} />,
        width: 280,
        render: (_, row) => {
          const category = findCascaderOptionById(
            row.productCategoryId,
            productCategoryCascaderOptions,
          );
          const mateList = getMaterialOptionsForCategory(
            row.productCategoryId,
            productCategoryCascaderOptions,
            materialCascaderOptionsMap,
          );

          return (
            <div className="space-y-1.5">
              <SpecRow label={<Trans i18nKey={LOCALS.pwr_category} />}>
                {category?.label || '-'}
              </SpecRow>
              <SpecRow label={<Trans i18nKey={LOCALS.rank} />}>
                {renderOptionTags(row.ranks, rankSelectOptions)}
              </SpecRow>
              <SpecRow label={<Trans i18nKey={LOCALS.color} />}>
                {renderOptionTags(row.color, colorSelectOptions)}
              </SpecRow>
              <SpecRow label={<Trans i18nKey={LOCALS.hardware} />}>
                {renderOptionTags(row.hardware, hardwareSelectOptions)}
              </SpecRow>
              <SpecRow label={<Trans i18nKey={LOCALS.stamp} />}>
                {renderOptionTags(row.stamp, stampSelectOptions)}
              </SpecRow>
              <SpecRow label={<Trans i18nKey={LOCALS.material} />}>
                {row.materials?.length ? (
                  <TagCell>
                    {row.materials.map((code) => {
                      const label =
                        findCascaderOptionById(code, mateList)?.label || code;
                      return (
                        <Tag
                          key={code}
                          className="m-0 max-w-full whitespace-normal"
                        >
                          {label}
                        </Tag>
                      );
                    })}
                  </TagCell>
                ) : (
                  '-'
                )}
              </SpecRow>
            </div>
          );
        },
      },
      {
        title: <Trans i18nKey={LOCALS.remark} />,
        dataIndex: 'remark',
        width: 320,
        render: (v?: string) =>
          v ? (
            <div className="max-w-80 whitespace-pre-wrap break-words">{v}</div>
          ) : (
            '-'
          ),
      },
      {
        title: <Trans i18nKey={LOCALS.pwr_source} />,
        dataIndex: 'source',
        width: 100,
        render: (value: PmsProductWantRequestSource) =>
          getWantRequestSourceLabel(value, t),
      },
      {
        title: <Trans i18nKey={LOCALS.created_by} />,
        dataIndex: 'createdBy',
        width: 100,
        render: (v?: string) => v || '-',
      },
      {
        title: <Trans i18nKey={LOCALS.created_time} />,
        dataIndex: 'createTime',
        width: 170,
        render: (time: string) => dayjs(time).format('YYYY-MM-DD HH:mm:ss'),
      },
      {
        title: <Trans i18nKey={LOCALS.option} />,
        key: 'operate',
        width: 200,
        fixed: 'right',
        render: (_, row) => (
          <Space size={[4, 0]} wrap className="max-w-full">
            <Button
              type="link"
              size="small"
              className="px-1"
              onClick={() => setEditRecord(row)}
            >
              <Trans i18nKey={LOCALS.edit} />
            </Button>
            <Button
              type="link"
              size="small"
              className="px-1"
              onClick={() => setMatchRequest({ id: row.id, email: row.email })}
            >
              <Trans i18nKey={LOCALS.pwr_match} />
            </Button>
            <Button
              type="link"
              size="small"
              className="px-1"
              onClick={() => setOperateLogRequestId(row.id)}
            >
              <Trans i18nKey={LOCALS.pwr_log} />
            </Button>
            {isAdmin && row.deleteStatus !== 1 ? (
              <Popconfirm
                title={<Trans i18nKey={LOCALS.delete} />}
                description={
                  <div className="w-[150px]">
                    <Trans i18nKey={LOCALS.are_you_sure_to_delete} />
                  </div>
                }
                onConfirm={() => handleDelete(row.id)}
              >
                <Button type="link" size="small" danger className="px-1">
                  <Trans i18nKey={LOCALS.delete} />
                </Button>
              </Popconfirm>
            ) : null}
          </Space>
        ),
      },
    ],
    [
      productCategoryCascaderOptions,
      rankSelectOptions,
      colorSelectOptions,
      hardwareSelectOptions,
      stampSelectOptions,
      materialCascaderOptionsMap,
      handleStatusSelect,
      handleDelete,
      statusSelectOptions,
      isAdmin,
      t,
    ],
  );

  return (
    <div className="space-y-4">
      <EditDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={getDataSource}
      />
      <EditDrawer
        open={!!editRecord}
        record={editRecord}
        onClose={() => setEditRecord(undefined)}
        onSuccess={getDataSource}
      />
      <MatchDrawer
        open={!!matchRequest}
        requestId={matchRequest?.id}
        customerEmail={matchRequest?.email}
        onClose={() => setMatchRequest(undefined)}
        onEmailSent={getDataSource}
      />
      <OperateLogDrawer
        open={!!operateLogRequestId}
        requestId={operateLogRequestId}
        onClose={() => setOperateLogRequestId(undefined)}
      />
      <StatusUpdateModal
        pending={pendingStatusChange}
        onClose={() => setPendingStatusChange(undefined)}
        onSuccess={() => {
          message.success(t(LOCALS.pwr_status_updated));
          getDataSource();
        }}
      />

      <Card bodyStyle={{ padding: '16px' }}>
        <Form
          form={form}
          layout="inline"
          onFinish={(values) => {
            const leafCategoryId = values.productCategoryIdList?.length
              ? values.productCategoryIdList[
                  values.productCategoryIdList.length - 1
                ]
              : undefined;
            const materialValues = (values.materialPaths ?? [])
              .map((path) => String(path[path.length - 1]))
              .filter(Boolean);

            setEmail(values.email || '');
            setPhone(values.phone || '');
            setStatus(values.status || '');
            setSource(values.source || '');
            setProductCategoryId(leafCategoryId ?? null);
            setRanks(values.ranks?.length ? values.ranks : null);
            setColor(values.color?.length ? values.color : null);
            setHardware(values.hardware?.length ? values.hardware : null);
            setStamp(values.stamp?.length ? values.stamp : null);
            setMaterials(materialValues.length ? materialValues : null);
            setRemark(values.remark || '');
            setPageNum(1);
          }}
        >
          <Form.Item label={<Trans i18nKey={LOCALS.email} />} name="email">
            <Input allowClear style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label={<Trans i18nKey={LOCALS.pwr_phone} />} name="phone">
            <Input allowClear style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label={<Trans i18nKey={LOCALS.status} />} name="status">
            <Select
              allowClear
              style={{ width: 200 }}
              options={statusSelectOptions}
              placeholder={t(LOCALS.all) || ''}
            />
          </Form.Item>
          <Form.Item
            label={<Trans i18nKey={LOCALS.pwr_source} />}
            name="source"
          >
            <Select
              allowClear
              style={{ width: 200 }}
              options={sourceSelectOptions}
              placeholder={t(LOCALS.all) || ''}
            />
          </Form.Item>
          <Form.Item
            label={<Trans i18nKey={LOCALS.pwr_category} />}
            name="productCategoryIdList"
          >
            <Cascader
              style={{ minWidth: 200 }}
              options={productCategoryCascaderOptions}
              showSearch={{ filter: cascaderFilter }}
              placeholder={t(LOCALS.please_select) || ''}
              onChange={(value) => {
                const newFirst = value?.[0];
                const oldFirst = filterCategoryIdList?.[0];
                if (String(newFirst ?? '') !== String(oldFirst ?? '')) {
                  form.setFieldValue('materialPaths', []);
                }
              }}
            />
          </Form.Item>
          <Form.Item
            label={<Trans i18nKey={LOCALS.material} />}
            name="materialPaths"
          >
            <Cascader
              multiple
              style={{ minWidth: 200 }}
              options={filterMaterialCascaderOptions}
              showSearch={{ filter: cascaderFilter }}
              placeholder={
                filterFirstLevelId
                  ? t(LOCALS.please_select) || ''
                  : t(LOCALS.please_select_category_first) || ''
              }
            />
          </Form.Item>
          <Form.Item label={<Trans i18nKey={LOCALS.rank} />} name="ranks">
            <Select
              mode="multiple"
              allowClear
              style={{ width: 200 }}
              options={rankOptions}
              placeholder={t(LOCALS.please_select) || ''}
            />
          </Form.Item>
          <Form.Item label={<Trans i18nKey={LOCALS.color} />} name="color">
            <Select
              mode="multiple"
              allowClear
              showSearch
              style={{ width: 200 }}
              options={colorSelectOptions}
              filterOption={optionFilter}
              placeholder={t(LOCALS.please_select) || ''}
            />
          </Form.Item>
          <Form.Item
            label={<Trans i18nKey={LOCALS.hardware} />}
            name="hardware"
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              style={{ width: 200 }}
              options={hardwareOptions}
              filterOption={optionFilter}
              placeholder={t(LOCALS.please_select) || ''}
            />
          </Form.Item>
          <Form.Item label={<Trans i18nKey={LOCALS.stamp} />} name="stamp">
            <Select
              mode="multiple"
              allowClear
              showSearch
              style={{ width: 200 }}
              options={stampOptions}
              filterOption={optionFilter}
              placeholder={t(LOCALS.please_select) || ''}
            />
          </Form.Item>

          <Form.Item label={<Trans i18nKey={LOCALS.remark} />} name="remark">
            <Input.TextArea rows={3} allowClear style={{ width: 200 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                <Trans i18nKey={LOCALS.search} />
              </Button>
              <Button
                htmlType="button"
                onClick={() => {
                  form.resetFields();
                  setEmail('');
                  setPhone('');
                  setStatus('');
                  setSource('');
                  setProductCategoryId(null);
                  setRanks(null);
                  setColor(null);
                  setHardware(null);
                  setStamp(null);
                  setMaterials(null);
                  setRemark('');
                  setPageNum(1);
                  setPageSize(10);
                }}
              >
                <Trans i18nKey={LOCALS.reset} />
              </Button>
              <Button type="primary" onClick={() => setCreateOpen(true)}>
                <Trans i18nKey={LOCALS.pwr_create} />
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card bodyStyle={{ padding: '16px' }}>
        <Table
          bordered
          scroll={{ x: 'max-content' }}
          rowKey="id"
          loading={loading}
          dataSource={dataSource}
          columns={columns}
          pagination={{
            showTotal,
            total,
            current: pageNum,
            pageSize,
            onChange: (page, size) => {
              setPageNum(page);
              setPageSize(size);
            },
          }}
        />
      </Card>
    </div>
  );
};

export default ProductWantRequestPage;
